'use client';

import { Component, ErrorInfo, ReactNode } from 'react';
import { captureException, getClient } from '@sentry/nextjs';

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  name?: string;
  onError?: (error: Error, info: ErrorInfo) => void;
  /** When any entry changes, clears `hasError` so children render again. */
  resetKeys?: ReadonlyArray<unknown>;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

function resetKeysChanged(
  a: ReadonlyArray<unknown> | undefined,
  b: ReadonlyArray<unknown> | undefined,
): boolean {
  if (a === b) return false;
  if (!a || !b) return true;
  if (a.length !== b.length) return true;
  for (let i = 0; i < a.length; i += 1) {
    if (!Object.is(a[i], b[i])) return true;
  }
  return false;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (
      this.state.hasError &&
      resetKeysChanged(prevProps.resetKeys, this.props.resetKeys)
    ) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const { name, onError } = this.props;
    let reported = false;
    try {
      // React error boundaries swallow the error before the SDK's global
      // handler sees it — without this explicit capture, errors caught here
      // never reach Vitals. No-ops when Sentry.init hasn't run (dev).
      captureException(error, {
        contexts: {
          react: {
            boundary: name ?? 'unnamed',
            componentStack: (info.componentStack ?? '').slice(0, 4000),
          },
        },
      });
      // Whether that capture went ANYWHERE. getClient() is undefined unless
      // init() ran, which covers every way reporting can be off: the deploy-env
      // gate, an empty NEXT_PUBLIC_VITALS_DSN on a real deploy, or a failed
      // init. Asking the SDK beats re-deriving the answer from env vars, which
      // could disagree with what actually happened.
      reported = Boolean(getClient());
    } catch {
      /* must not throw */
    }

    // Logged exactly when the error reached NOBODY — i.e. local work, where
    // this is the only signal (a local production build has no dev overlay and
    // no longer reports). Two earlier versions of this gate were wrong in the
    // same direction: `NODE_ENV !== 'production'` silently skipped local
    // production builds, and `!shouldReportToVitals()` read the env rather than
    // the outcome, so disabling reporting by blanking the DSN on a real deploy
    // would have suppressed the console too and made caught errors vanish
    // completely. Gating on the actual client means this can only ever fall
    // back to louder, never to silent.
    //
    // Deliberately NOT unconditional, unlike app/error.tsx and
    // app/global-error.tsx: those are one-per-page boundaries, whereas this one
    // nests. A single failing subtree can trip several instances, and since
    // console breadcrumbs are captured (trimmed) that would emit N
    // self-referential breadcrumbs, crowding the real pre-error trail out of
    // the buffer on precisely the events where it matters. It would also print
    // a 4000-char component stack into real visitors' devtools.
    if (!reported) {
      console.error(
        `[ErrorBoundary:${name ?? 'unnamed'}] caught render error`,
        error,
        info,
      );
    }

    if (onError) {
      try {
        onError(error, info);
      } catch {
        /* must not throw */
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
